// Composant Liste de Progression Management (Q2)
// Affiche les 6 axes du questionnaire management avec barres de progression

import { MANAGEMENT_AXES, type ScoresManagement } from './RadarChartManagement'

export interface ManagementProgressListProps {
  scores: ScoresManagement
  maxScores: ScoresManagement
  showPercentage?: boolean
  compact?: boolean
}

// Couleurs personnalisees pour chaque axe management
const AXES_COLORS = {
  orientations: { bg: 'bg-blue-50', bar: 'bg-blue-500', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
  roles: { bg: 'bg-purple-50', bar: 'bg-purple-500', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
  gestion: { bg: 'bg-green-50', bar: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' },
  animation: { bg: 'bg-amber-50', bar: 'bg-amber-500', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
  bilan: { bg: 'bg-cyan-50', bar: 'bg-cyan-500', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700' },
  audits: { bg: 'bg-rose-50', bar: 'bg-rose-500', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' },
}

export default function ManagementProgressList({
  scores,
  maxScores,
  showPercentage = true,
  compact = false,
}: ManagementProgressListProps) {
  // Calculer les donnees pour chaque axe
  const axesData = MANAGEMENT_AXES.map((axe) => {
    const score = scores[axe.key] || 0
    const max = maxScores[axe.key] || 0
    const percent = max > 0 ? Math.round((score / max) * 100) : 0

    return {
      ...axe,
      score,
      max,
      percent,
      colors: AXES_COLORS[axe.key] || { bg: 'bg-gray-50', bar: 'bg-gray-500', text: 'text-gray-700' },
    }
  })

  // Determiner la couleur de la barre en fonction du pourcentage
  const getProgressColor = (percent: number) => {
    if (percent >= 70) return 'bg-green-500'
    if (percent >= 40) return 'bg-orange-500'
    return 'bg-red-500'
  }

  // Calculer le total
  const totalScore = axesData.reduce((sum, a) => sum + a.score, 0)
  const totalMax = axesData.reduce((sum, a) => sum + a.max, 0)
  const totalPercent = totalMax > 0 ? Math.round((totalScore / totalMax) * 100) : 0

  if (compact) {
    return (
      <div className="space-y-2">
        {axesData.map((axe) => (
          <div key={axe.key} className="group">
            <div className="flex items-center gap-2 text-xs mb-1">
              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {axe.label.charAt(0)}
              </span>
              <span className="flex-1 text-gray-700">{axe.label}</span>
              <span className="font-medium text-gray-900">{axe.percent}%</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor(axe.percent)} transition-all`}
                style={{ width: `${axe.percent}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {axesData.map((axe) => (
        <div key={axe.key} className={`p-3 rounded-lg ${axe.colors.bg}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className={`text-sm font-medium ${axe.colors.text}`}>
                {axe.label}
              </span>
            </div>
            <div className="flex items-center gap-2">
              {showPercentage && (
                <span className={`text-lg font-bold ${axe.colors.text}`}>
                  {axe.percent}%
                </span>
              )}
              <span className="text-xs text-gray-500">
                ({axe.score}/{axe.max})
              </span>
            </div>
          </div>

          {/* Barre de progression */}
          <div className="h-2.5 bg-white/60 rounded-full overflow-hidden">
            <div
              className={`h-full ${getProgressColor(axe.percent)} transition-all duration-500`}
              style={{ width: `${axe.percent}%` }}
            />
          </div>
        </div>
      ))}

      {/* Total */}
      <div className="mt-4 pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Score total Management</span>
          <div className="flex items-center gap-2">
            <span className="text-lg font-bold text-primary-600">{totalPercent}%</span>
            <span className="text-sm text-gray-500">({totalScore}/{totalMax})</span>
          </div>
        </div>
        <div className="mt-2 h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full ${getProgressColor(totalPercent)} transition-all duration-500`}
            style={{ width: `${totalPercent}%` }}
          />
        </div>
      </div>
    </div>
  )
}

// Version Top 3 / Flop 3 pour Q2
export function ManagementRanking({
  scores,
  maxScores,
}: Pick<ManagementProgressListProps, 'scores' | 'maxScores'>) {
  const axesData = MANAGEMENT_AXES.map((axe) => {
    const score = scores[axe.key] || 0
    const max = maxScores[axe.key] || 0
    const percent = max > 0 ? Math.round((score / max) * 100) : 0
    return { ...axe, score, max, percent }
  })

  const sorted = [...axesData].sort((a, b) => b.percent - a.percent)
  const top3 = sorted.slice(0, 3)
  const flop3 = sorted.slice(-3).reverse()

  return (
    <div className="space-y-3">
      {/* Top 3 */}
      <div>
        <h4 className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
          Points forts
        </h4>
        <div className="space-y-1">
          {top3.map((axe, i) => (
            <div key={axe.key} className="flex items-center gap-2 text-xs">
              <span className="w-4 h-4 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                {i + 1}
              </span>
              <span className="flex-1 truncate">{axe.label}</span>
              <span className="font-medium text-green-700">{axe.percent}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Flop 3 */}
      <div>
        <h4 className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
          A ameliorer
        </h4>
        <div className="space-y-1">
          {flop3.map((axe, i) => (
            <div key={axe.key} className="flex items-center gap-2 text-xs">
              <span className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold">
                {i + 1}
              </span>
              <span className="flex-1 truncate">{axe.label}</span>
              <span className="font-medium text-red-700">{axe.percent}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
